import {
  createMaterialPricing,
  deleteMaterialPricing,
  getMaterialAssembly,
  getMaterialAssemblyByProjectId,
  getMaterialPricing,
  saveMaterialAssembly,
  saveMaterialAssemblyForProject,
  updateMaterialPricing,
} from 'api/MaterialsApi'
import type { Dispatch } from 'redux'
import { setProFeatureError, setErrorMessage } from 'reduxStore/projects/actions'
import { pushToDataLayer } from 'utils/eventPushes'
import {
  createCrew as createCrewAPI,
  deleteCrew as deleteCrewAPI,
  getCrews as getCrewsAPI,
  updateCrew as updateCrewAPI,
} from 'api/CrewsApi'
import {
  deleteMaterial as deleteMaterialAPI,
  deleteProjectMaterials as deleteProjectMaterialsAPI,
  editMaterial as editMaterialAPI,
  editMaterialsGroup as editMaterialsGroupAPI,
  getMaterialsGroups as getMaterialsGroupsAPI,
  getProjectMaterials as getProjectMaterialsAPI,
  getUserMaterials as getUserMaterialsAPI,
  postMaterial as postMaterialAPI,
  postMaterialsGroups as postMaterialsGroupsAPI,
  postProjectMaterial as postProjectMaterialAPI,
} from '../../api/ProjectsAPI'
import {
  addMaterialsGroupSuccess,
  deleteCrewSuccess,
  deleteMaterialPricingSuccess,
  deleteMaterialsSuccess,
  deleteProjectMaterialSuccess,
  editCrewSuccess,
  editMaterialPricingSuccess,
  editMaterialsGroupSuccess,
  editMaterialsSuccess,
  getCrewsSuccess,
  getMaterialAssembliesSuccess,
  getMaterialPricingSuccess,
  getMaterialsGroupsSuccess,
  getMaterialsSuccess,
  getProjectMaterialAssembliesSuccess,
  getProjectMaterialsSuccess,
  materialPricingAddedSuccess,
  postCrewSuccess,
  postMaterialsSuccess,
} from './actions'
import type {
  AddedGroup,
  AddedMaterial,
  Crew,
  Group,
  Material,
  MaterialAssembly,
  MaterialsState,
  ProjectMaterial,
} from './types'
import { deleteProposalMaterial } from 'api/ProposalApi'

export const getMaterialsGroups = () => (dispatch: Dispatch) => {
  return getMaterialsGroupsAPI()
    .then((res) => {
      dispatch(getMaterialsGroupsSuccess(res.data))
      return res
    })
    .catch((err) => {
      console.error(`getMaterialsGroups - ${err}`)
    })
}

export const addMaterialGroup = (group: Group) => (dispatch: Dispatch) => {
  return postMaterialsGroupsAPI(group).then((res) => {
    dispatch(addMaterialsGroupSuccess(res.data))
    return res
  })
}

export const editMaterialGroup = (group: AddedGroup) => (dispatch: Dispatch) => {
  return editMaterialsGroupAPI(group).then((res) => {
    dispatch(editMaterialsGroupSuccess(res.data))
    return res
  })
}

export const getMaterials = () => (dispatch: Dispatch) => {
  return getUserMaterialsAPI()
    .then((res) => {
      dispatch(getMaterialsSuccess(res.data))
      return res
    })
    .catch((err) => {
      console.error(`getMaterials - ${err}`)
    })
}

export const postMaterial = (material: Material) => (dispatch: Dispatch) => {
  return postMaterialAPI(material).then((res) => {
    dispatch(postMaterialsSuccess(res.data as AddedMaterial))
    return res
  })
}

export const editMaterial = (material: Material) => (dispatch: Dispatch) => {
  return editMaterialAPI(material).then((res) => {
    dispatch(editMaterialsSuccess(res.data as AddedMaterial))
    return res
  })
}

export const deleteMaterial = (materialId: AddedMaterial['id']) => (dispatch: Dispatch) => {
  return deleteMaterialAPI(materialId).then((res) => {
    dispatch(deleteMaterialsSuccess(materialId))
    return res
  })
}

export const deleteProjectMaterial = (projectId: string, id: ProjectMaterial['id']) => (dispatch: Dispatch) => {
  return deleteProjectMaterialsAPI(projectId, id).then((res) => {
    dispatch(deleteProjectMaterialSuccess(id))
    return res
  })
}

export const getProjectMaterials = (projectId: ProjectMaterial['projectId']) => (dispatch: Dispatch) => {
  return getProjectMaterialsAPI(projectId).then((res) => {
    dispatch(getProjectMaterialsSuccess(res.data))
    return res
  })
}

export const addProjectMaterial = (projectId: string, materialIds: string[]) => (dispatch: Dispatch) => {
  return postProjectMaterialAPI(projectId, materialIds).then((res) => {
    getProjectMaterials(projectId)(dispatch)
    return res
  })
}

export const handleAttachMaterialToGroup =
  (materialsGroups: MaterialsState['groups'], materialData: AddedMaterial) => (dispatch: Dispatch) => {
    materialsGroups.forEach((group) => {
      const modifiedMaterialAttachedToGroup =
        materialData.groups && materialData.groups!.length > 0
          ? materialData.groups!.find((modifiedGroup) => modifiedGroup.id === group.id)
          : null

      // delete material from group
      if (group.materialIds?.includes(materialData.id) && !modifiedMaterialAttachedToGroup) {
        editMaterialGroup({
          ...group,
          materialIds: group.materialIds.filter((materialId) => materialId !== materialData.id),
        })(dispatch)
      }

      // add new material to group
      if ((!group.materialIds || !group.materialIds?.includes(materialData.id)) && modifiedMaterialAttachedToGroup) {
        editMaterialGroup({
          ...group,
          materialIds: [...new Set(group.materialIds ? group.materialIds.concat(materialData.id) : [materialData.id])],
        })(dispatch)
      }
    })
  }

//CREATE methods required for Material assmebly
export const getMaterialAssemblies = () => (dispatch: Dispatch) => {
  getMaterialAssembly()
    .then((res) => {
      dispatch(getMaterialAssembliesSuccess(res.data))
      return res
    })
    .catch((err) => {
      console.error(`getMaterialAssemblies - ${err}`)
    })
}
export const addMaterialAssembly = (materialAssembly: MaterialAssembly) => (dispatch: Dispatch) => {
  return saveMaterialAssembly(materialAssembly)
    .then(() => {
      return getMaterialAssemblies()(dispatch)
    })
    .catch((error: any) => {
      // Handle 402 pro feature errors
      if (error.response?.status === 402) {
        const featureName = error.response?.data?.featureName || 'Assemblies'
        dispatch(setProFeatureError(featureName))

        // Track pro feature blocked event
        pushToDataLayer({
          event: 'pro_feature_blocked',
          feature_name: featureName,
        })
      } else {
        // Handle other errors normally
        dispatch(setErrorMessage(error.message))
      }
      throw error
    })
}

export const updateMaterialAssembly = (materialAssembly: MaterialAssembly) => (dispatch: Dispatch) => {
  saveMaterialAssembly(materialAssembly).then(() => {
    return getMaterialAssemblies()(dispatch)
  })
}

export const getMaterialAssembliesForProject = (projectId: string) => (dispatch: Dispatch) => {
  console.log('Fetching material assemblies for project:', projectId)
  getMaterialAssemblyByProjectId(projectId)
    .then((res) => {
      console.log('Fetched material assemblies:', res.data)
      dispatch(getProjectMaterialAssembliesSuccess(res.data))
      return res
    })
    .catch((err) => {
      console.error(`getMaterialAssembliesForProject - ${err}`)
    })
}

export const addMaterialAssemblyForProject = (projectId: string, materialAssemblyIds: string[]) => (dispatch: Dispatch) => {
  return saveMaterialAssemblyForProject(projectId, materialAssemblyIds).then((res) => {
    getMaterialAssembliesForProject(projectId)(dispatch)
    return res
  })
}

export const getUserMaterialPricing = () => (dispatch: Dispatch) => {
  return getMaterialPricing().then((res) => {
    dispatch(getMaterialPricingSuccess(res.data))
    return res
  })
}

export const addUserMaterialPricing = (material: AddedMaterial) => (dispatch: Dispatch) => {
  return createMaterialPricing(material).then((res) => {
    dispatch(materialPricingAddedSuccess(res.data))
    return res
  })
}

export const editUserMaterialPricing = (id: string, materialPricing: any) => (dispatch: Dispatch) => {
  return updateMaterialPricing(id, materialPricing).then((res) => {
    dispatch(editMaterialPricingSuccess(res.data))
    return res
  })
}

export const deleteUserMaterialPricing = (id: string) => (dispatch: Dispatch) => {
  return deleteMaterialPricing(id).then((res) => {
    dispatch(deleteMaterialPricingSuccess(id))
    return res
  })
}

export const deleteProposalMaterialPricing = (id: string) => (dispatch: Dispatch) => {
  return deleteProposalMaterial(id).then((res) => {
    // dispatch(deleteMaterialPricingSuccess(id))
    return res
  })
}

export const getCrews = () => (dispatch: Dispatch) => {
  return getCrewsAPI()
    .then((res) => {
      dispatch(getCrewsSuccess(res.data))
      return res
    })
    .catch((err) => {
      console.error(`getCrews - ${err}`)
    })
}

export const addCrew =
  (crew: Omit<Crew, 'id' | 'userId' | 'createdDate'>) => (dispatch: Dispatch) => {
    return createCrewAPI(crew)
      .then((res) => {
        dispatch(postCrewSuccess(res.data))
        pushToDataLayer({
          event: 'crew_added',
          crew_name: res.data.name,
          crew_id: res.data.id,
        })
        return res
      })
      .catch((error: any) => {
        if (error.response?.status === 402) {
          const featureName = error.response?.data?.featureName || 'Crews'
          dispatch(setProFeatureError(featureName))
          pushToDataLayer({
            event: 'pro_feature_blocked',
            feature_name: featureName,
          })
        } else {
          dispatch(setErrorMessage(error.message))
        }
        throw error
      })
  }

export const editCrew = (id: string, crew: Crew) => (dispatch: Dispatch) => {
  return updateCrewAPI(id, crew).then((res) => {
    dispatch(editCrewSuccess(res.data))
    return res
  })
}

export const removeCrew = (id: string) => (dispatch: Dispatch) => {
  return deleteCrewAPI(id).then((res) => {
    dispatch(deleteCrewSuccess(id))
    return res
  })
}
